import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { UnitExam, Unit } from '@/types'
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  GraduationCap,
  AlertCircle,
  Clock,
  HelpCircle
} from 'lucide-react'

interface ExamWithUnit extends UnitExam {
  unit: Unit
  questions_count: number
}

export default function ExamsList() {
  const [exams, setExams] = useState<ExamWithUnit[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; exam: UnitExam | null }>({
    open: false,
    exam: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    loadExams()
  }, [])

  const loadExams = async () => {
    try {
      const { data, error } = await supabase
        .from('unit_exams')
        .select(`
          *,
          unit:units(*),
          questions:exam_questions(count)
        `)
        .order('unit_id', { ascending: true })

      if (error) throw error

      const examsWithCount = (data || []).map((e: any) => ({
        ...e,
        title: e.title_es || e.title_fr || e.title || '',
        description: e.description_es || e.description_fr || e.description || '',
        time_limit: e.time_limit_minutes ?? e.time_limit ?? 30,
        questions_count: e.questions?.[0]?.count || 0,
        unit: e.unit ? {
          ...e.unit,
          title: e.unit.title || e.unit.title_es || e.unit.title_fr || e.unit.name_es || e.unit.name_fr || '',
          order_num: e.unit.order_num ?? e.unit.unit_number ?? 0,
        } : null
      }))

      setExams(examsWithCount)
    } catch (error) {
      console.error('Error loading exams:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteModal.exam) return

    setIsDeleting(true)
    try {
      const { error } = await supabase
        .from('unit_exams')
        .delete()
        .eq('id', deleteModal.exam.id)

      if (error) throw error

      setExams(exams.filter(e => e.id !== deleteModal.exam?.id))
      setDeleteModal({ open: false, exam: null })
    } catch (error) {
      console.error('Error deleting exam:', error)
      alert('Error al eliminar el examen')
    } finally {
      setIsDeleting(false)
    }
  }

  const filteredExams = exams.filter(exam =>
    (exam.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (exam.unit?.title || '').toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exámenes</h1>
          <p className="text-gray-600">Gestiona los exámenes de unidad</p>
        </div>
        <Link
          to="/admin/exams/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nuevo Examen
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar exámenes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
        />
      </div>

      {/* Exams Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredExams.length > 0 ? (
          filteredExams.map((exam) => (
            <div key={exam.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 rounded-lg bg-purple-100">
                    <GraduationCap className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    exam.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {exam.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>

                <h3 className="font-semibold text-gray-900 mb-1">{exam.title}</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Unidad {exam.unit?.order_num}: {exam.unit?.title}
                </p>

                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-1">
                    <HelpCircle className="w-4 h-4" />
                    <span>{exam.questions_count} preguntas</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{exam.time_limit} min</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-sm">
                    Aprobar: <span className="font-medium text-purple-600">{exam.passing_score}%</span>
                  </span>
                  <div className="flex items-center gap-1">
                    <Link
                      to={`/admin/exams/${exam.id}`}
                      className="p-2 text-gray-500 hover:text-[#0055A4] hover:bg-[#0055A4]/5 rounded-lg transition-colors"
                      title="Visualizar"
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                    <Link
                      to={`/admin/exams/${exam.id}/edit`}
                      className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => setDeleteModal({ open: true, exam })}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No se encontraron exámenes</p>
            <Link
              to="/admin/exams/new"
              className="text-[#0055A4] hover:underline text-sm mt-2 inline-block"
            >
              Crear primer examen
            </Link>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-red-100">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Eliminar Examen</h3>
            </div>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar el examen "{deleteModal.exam?.title}"?
              Esta acción no se puede deshacer y eliminará todas las preguntas asociadas.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, exam: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
